import { useRef, useState } from 'react';

/**
 * THE badInput MACHINERY for a native `type="number"` box — shared by
 * `ui/CurrencyInput` (money) and `ui/NumberInput` (everything else).
 * Extracted VERBATIM from `CurrencyInput.js` on 2026-10-28 (input audit HIGH 1 +
 * HIGH 2: only the money box had it, so every other number box handed `''` up
 * for text the browser could not read, and `''` means "follow the level above"
 * on the agreement figures and the crew O/T multipliers — pay silently changed).
 *
 * WHAT IT GUARANTEES (the rule: `ui/CurrencyInput.js`'s THE badInput RULE):
 * typed text the browser cannot read (`validity.badInput` — Firefox `1,000`,
 * `12abc`, `1.2.3`; every engine `-`, `1e`, `.`) emits the caller's `marker`,
 * NEVER `''`. The marker is non-blank and non-numeric, so `isMalformedAmount`
 * (`utils/moneyPayload.js`) answers TRUE for it and the caller's existing
 * validator refuses the save with its own message.
 *
 * 🪤 **THE MARKER IS DISPLAYED AS `''` WHILE FOCUSED (`focusedDisplay`), AND
 * THAT IS LOAD-BEARING.** React writes `value` into the DOM only when it differs
 * from `node.value`, which is `""` under badInput — so displaying `''` leaves
 * the user's own text on screen, while displaying the marker would make React
 * write it into a number input, which sanitises it to `""` and WIPES what they
 * typed. MEASURED (real React, real Firefox): with the marker displayed, typing
 * `1,000` over £1,234.56 wiped the box after the comma, the `000` still typed
 * then emitted `'000'`, and Save sent **`0`**. At REST the caller renders a
 * `type="text"` box and the marker's own text shows, written to read as an
 * error. The typed text itself is unrecoverable: Firefox exposes no API for a
 * number input's raw text.
 *
 * 🪤 **`onInput` IS NEEDED BECAUSE React's `onChange` IS DEDUPED ON
 * `node.value`.** Under badInput every keystroke leaves `node.value` at `""`, so
 * React fires no `onChange` for `abc` typed into an EMPTY field, nor for
 * CLEARING a box that held `1,000` — the second would strand the marker behind
 * an empty box. Wire `emitTyped(e, true)` to `onInput` AND `emitTyped(e, false)`
 * to `onChange`: one native event reaches both, deduped here, so one keystroke
 * is exactly one emit. `onChange` alone still serves a synthetic `change`.
 *
 * 🚨 **EXIT, THEN RE-ENTRY (review F1, 2026-09-23).** At rest the marker is in a
 * `type="text"` box; refocus flips it to `type="number"` and the browser
 * sanitises it to `""` behind React's value tracker, so Firefox's next native
 * `change` (blur, Enter) reported `""` as an edit and the save sent `null` —
 * the erase again, one gesture later. `emitTyped` ignores exactly that
 * `change`; a delete key in the empty box is the deliberate clear (F4,
 * `handleKeyDown`).
 *
 * 🔑 It owns NO blur: what a blur does (CurrencyInput snaps money to 2dp;
 * NumberInput never rounds) is each control's own rule. It owns the drop's
 * CLAIM (`dropHandler`: replace, never insert-at-caret — `CurrencyInput.js`'s
 * THE DROP RULING), and the caller passes what the dropped text BECOMES (money
 * strips `£`/`,`; a plain number is trimmed and held as typed).
 *
 * @param {{ value: any, onChange: (next: string) => void, marker: string, disabled?: boolean }} args
 * @returns {{ focused: boolean, setFocused: Function, rawValue: any, isMarker: boolean,
 *   focusedDisplay: any, emitTyped: Function, handleKeyDown: Function,
 *   dropHandler: (toValue: (text: string) => string) => Function }}
 */
export default function useUnreadableNumberEntry({ value, onChange, marker, disabled }) {
  const [focused, setFocused] = useState(false);
  const lastInputEventRef = useRef(null);
  const rawValue = value ?? '';
  const isMarker = rawValue === marker;

  /**
   * ONE emitter behind `onInput` AND `onChange`: a browser keystroke reaches
   * both through ONE native event, so the second call returns early. The
   * `next === rawValue` skip is for `onInput` only, which unlike React's
   * `onChange` is not deduped and would otherwise re-emit the marker on every
   * further bad keystroke.
   */
  const emitTyped = (e, fromInput) => {
    if (e.nativeEvent && lastInputEventRef.current === e.nativeEvent) return;
    lastInputEventRef.current = e.nativeEvent;
    // 🚨 F1: a native `change` reporting "" while the marker is held is the
    // BROWSER'S sanitisation of the marker on refocus (type text → number), not
    // an edit — emitting it sent `null` = CLEAR. A real clear arrives as an
    // `input` event (or the Backspace path below), so only `change` is ignored.
    if (!fromInput && e.nativeEvent && e.nativeEvent.type === 'change'
      && isMarker && e.target.value === '') return;
    const { value: typed, validity } = e.target;
    const next = validity && validity.badInput ? marker : typed;
    if (fromInput && next === rawValue) return;
    onChange(next);
  };

  /**
   * F4: after a refocus the marker sits behind a visibly EMPTY number box, and
   * Backspace/Delete in an empty box fire no `input` event — so without this
   * the only way out was to type a digit first. A delete key there is a
   * deliberate clear. NOT while `badInput` (the user's text is still on screen
   * and the browser's own `input` event owns that edit).
   */
  const handleKeyDown = (e) => {
    if (!isMarker || disabled) return;
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    const { value: shown, validity } = e.target;
    if (shown !== '' || (validity && validity.badInput)) return;
    onChange('');
  };

  /**
   * A drop REPLACES the whole value. The `preventDefault` is UNCONDITIONAL and
   * comes first: it replaces insert-at-caret, and it stops a stray file drop
   * navigating the tab away from a half-filled form. A file drag carries no
   * `text/plain`, and clearing a value the user typed is worse than ignoring
   * the gesture, so an empty drop emits nothing.
   */
  const dropHandler = (toValue) => (e) => {
    e.preventDefault();
    if (disabled) return;
    const dropped = e.dataTransfer ? e.dataTransfer.getData('text/plain') : '';
    if (!dropped || !dropped.trim()) return;
    onChange(toValue(dropped));
  };

  return {
    dropHandler,
    focused,
    setFocused,
    rawValue,
    isMarker,
    // 🪤 `''` for the marker — see above; never "simplify" it away.
    focusedDisplay: isMarker ? '' : rawValue,
    emitTyped,
    handleKeyDown,
  };
}
